/* The shop's jobs.
 *
 * One endpoint answers both the board and the jobs list, because they are the
 * same records asked two different questions: the board wants every card in
 * board order and cannot be paged, and the list wants a page of a growing
 * archive, sorted by whichever column the office clicked. Same permissions,
 * same ordering rules, one place they can be wrong.
 *
 * The server refuses what it should — a subcontractor is answered with the jobs
 * they are on rather than the shop's, and a write to a job you are not on comes
 * back 403 with a sentence to show as written — so nothing here re-checks. */
import type {
  CreateJobRequest,
  JobDto,
  JobsPageDto,
  ListJobsQuery,
  MoveJobRequest,
  MoveJobResponse,
  UpdateJobRequest,
} from '@oneshot2pac/shared';
import { api } from './client';
import { queryString } from './query';

export const jobsApi = {
  /** One page of the list — or, at `BOARD_PAGE_SIZE`, the whole board. */
  list: (query: ListJobsQuery = {}) => api.get<JobsPageDto>(`/jobs${queryString({ ...query })}`),

  get: (code: string) => api.get<JobDto>(`/jobs/${encodeURIComponent(code)}`),

  create: (body: CreateJobRequest) => api.post<JobDto>('/jobs', body),

  /** Send only what changed. A blank string clears a field; leaving it out
   *  leaves it alone. */
  update: (code: string, body: UpdateJobRequest) =>
    api.patch<JobDto>(`/jobs/${encodeURIComponent(code)}`, body),

  /** Move a card on the board. Answers with the whole target stage, renumbered:
   *  a move shifts the cards around it, and the board has to take all of them. */
  move: (code: string, body: MoveJobRequest) =>
    api.post<MoveJobResponse>(`/jobs/${encodeURIComponent(code)}/move`, body),
};
